import { useQuery } from "@hooks/useQuery";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { eramanBEApi, eramanCMSApi } from "~/Services";
import activeImg from "../../../../../Assets/SVG/activeImg.svg";
import campaignImg from "../../../../../Assets/SVG/campaignImg.svg";
import enquiryImg from "../../../../../Assets/SVG/enquiryImg.svg";
import popularImg from "../../../../../Assets/SVG/popularImg.svg";
import productImg from "../../../../../Assets/SVG/productImg.svg";
import totalImg from "../../../../../Assets/SVG/totalImg.svg";
import transactionImg from "../../../../../Assets/SVG/transactionImg.svg";
import userImg from "../../../../../Assets/SVG/userImg.svg";
import { AreaChartCard } from "./AreaChartCard";
import StatCard from "./StatCard";

export const EramanDashboard = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsers: 0,
    newEnquiries: 0,
    totalComments: 0,
    popularProducts: 0,
    activePromotions: 0,
    totalTransactions: 0,
  });

  const [dashDrpData, setDashDrpData] = useState({
    active_promotions: 0,
    top_selling_best_sellers: 0,
  });

  const [userGraphData, setUserGraphData] = useState([]);
  const [newsGraphData, setNewsGraphData] = useState([]);

  const currentYear = new Date().getFullYear();

  const [userSelectedYear, setUserSelectedYear] = useState(currentYear.toString());
  const [newsletterSelectedYear, setNewsletterSelectedYear] = useState(currentYear.toString());

  const fetchDashboardData = async () => {
    try {
      const response = await eramanBEApi.get(`/admin/dashboard`);
      setDashboardData(response?.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    }
  };

  const fetchDashboardDataDrupal = async () => {
    try {
      const response = await eramanCMSApi.get(`/api/dashboard/stats`);
      setDashDrpData(response.data.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    }
  };

  const fetchDashboardDataUser = async (year: string) => {
    try {
      const response: any = await eramanBEApi.get(`/admin/dashboard/user-graph?userGraphYear=${year}`);

      const monthMap: any = {
        1: "Jan",
        2: "Feb",
        3: "Mar",
        4: "Apr",
        5: "May",
        6: "Jun",
        7: "Jul",
        8: "Aug",
        9: "Sep",
        10: "Oct",
        11: "Nov",
        12: "Dec",
      };

      const formattedData: any[] = Object.entries(response?.data || {})
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([month, stats]: [any, any]) => ({
          name: monthMap[Number(month)] || `Month ${month}`,
          totalusers: stats?.totalUsers ?? 0,
          activeusers: stats?.activeUsers ?? 0,
          newusers: stats?.newUsers ?? 0,
          expiredusers: stats?.expiredUsers ?? 0, // if applicable
        }));

      setUserGraphData(formattedData);
    } catch (error: any) {
      console.error("Failed to fetch dashboard user graph data:", error);
    }
  };

  const fetchDashboardDataNews = async (year: string) => {
    try {
      const response: any = await eramanBEApi.get(`/admin/dashboard/newsletter-graph?newsletterGraphYear=${year}`);

      const monthMap: any = {
        1: "Jan",
        2: "Feb",
        3: "Mar",
        4: "Apr",
        5: "May",
        6: "Jun",
        7: "Jul",
        8: "Aug",
        9: "Sep",
        10: "Oct",
        11: "Nov",
        12: "Dec",
      };

      const formattedData: any[] = Object.entries(response?.data || {})
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([month, stats]: [any, any]) => ({
          name: monthMap[Number(month)] || `Month ${month}`,
          totalsubscribers: stats?.totalSubscribers ?? 0,
          guestsubscribers: stats?.guestSubscribers ?? 0,
          registeredsubscribers: stats?.registeredSubscribers ?? 0,
        }));

      setNewsGraphData(formattedData);
    } catch (error: any) {
      console.error("Failed to fetch dashboard newsletter graph data:", error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchDashboardDataDrupal();
    fetchDashboardDataUser(userSelectedYear);
    fetchDashboardDataNews(newsletterSelectedYear);
  }, [userSelectedYear, newsletterSelectedYear]);

  const statsData = [
    { id: 1, image: totalImg, title: "Total Users", count: dashboardData.totalUsers },
    { id: 2, image: activeImg, title: "Active Users", count: dashboardData.activeUsers },
    { id: 3, image: userImg, title: "New Users", count: dashboardData.newUsers },
    { id: 4, image: enquiryImg, title: "New Enquiries", count: dashboardData.newEnquiries },
    { id: 5, image: popularImg, title: "Popular Products", count: dashDrpData.top_selling_best_sellers },
    { id: 6, image: campaignImg, title: "Active Campaigns", count: dashDrpData.active_promotions },
    { id: 7, image: productImg, title: "Comments", count: dashboardData.totalComments },
    {
      id: 8,
      image: transactionImg,
      title: "Total Transactions",
      count: dashboardData.totalTransactions ? `RM ${dashboardData.totalTransactions}` : "RM 0",
    },
  ];

  return (
    <ContentWrapper title="Dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {statsData.map((stat) => (
          <StatCard
            key={stat.id}
            image={stat.image}
            title={stat.title}
            count={stat.count}
          />
        ))}
      </div>

      <div className="flex flex-wrap justify-between mt-5">
        <div className="w-full sm:w-1/2 p-2">
          <AreaChartCard
            data={userGraphData}
            title="Users"
            userTypes={["Total Users", "Active Users", "New Users", "Expired Users"]}
            selectedYear={userSelectedYear}
            onYearChange={setUserSelectedYear}
          />
        </div>

        <div className="w-full sm:w-1/2 p-2">
          <AreaChartCard
            data={newsGraphData}
            title="Newsletter Subscription"
            userTypes={["Total Subscribers", "Guest Subscribers", "Registered Subscribers"]}
            selectedYear={newsletterSelectedYear}
            onYearChange={setNewsletterSelectedYear}
          />
        </div>
      </div>
    </ContentWrapper>
  );
};
